import React, { useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { brandColors } from '../Theme';
import {
    EXPLORE_ITEMS,
    EXPLORE_MENU_LABEL,
    NAV_ITEMS,
    isExplorePath,
} from '../navigation';

export const floatingNavSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    px: { xs: 2, md: 2.5 },
    py: { xs: 1.15, md: 1.25 },
    bgcolor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 2,
    backdropFilter: 'blur(12px)',
};

const navShadowSx = {
    hero: {
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
    },
    interior: {
        boxShadow: '0 2px 12px rgba(15, 23, 42, 0.05)',
        border: '1px solid rgba(15, 23, 42, 0.06)',
    },
} as const;

type FloatingNavProps = {
    menuOpen?: boolean;
    onOpenMenu?: () => void;
    variant?: keyof typeof navShadowSx;
};

const navButtonSx = (isActive: boolean) => ({
    textTransform: 'none' as const,
    fontWeight: isActive ? 700 : 500,
    fontSize: '0.9375rem',
    color: isActive ? brandColors.footerBlue : 'rgba(26, 26, 26, 0.75)',
    px: 1.5,
    py: 0.75,
    borderRadius: 1.5,
    bgcolor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
    '&:hover': {
        bgcolor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
    },
});

const FloatingNav: React.FC<FloatingNavProps> = ({
    menuOpen = false,
    onOpenMenu = () => {},
    variant = 'hero',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const isFlushMobileBar = isMobile && variant === 'interior';
    const [exploreAnchor, setExploreAnchor] = useState<null | HTMLElement>(null);
    const exploreOpen = Boolean(exploreAnchor);
    const isExploreActive = isExplorePath(location.pathname);

    const handleExploreOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setExploreAnchor(event.currentTarget);
    };

    const handleExploreClose = () => {
        setExploreAnchor(null);
    };

    return (
        <Box
            sx={{
                ...floatingNavSx,
                ...(isFlushMobileBar
                    ? {
                          borderRadius: 0,
                          boxShadow: 'none',
                          border: 'none',
                          bgcolor: '#fff',
                          backdropFilter: 'none',
                          mx: 0,
                      }
                    : { ...navShadowSx[variant], mx: { xs: 0, md: 0.5 } }),
            }}
        >
            <Typography
                component={RouterLink}
                to="/"
                sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    lineHeight: 1.2,
                    color: '#1a1a1a',
                    letterSpacing: '-0.01em',
                    textDecoration: 'none',
                    minWidth: 0,
                    '&:hover': { color: brandColors.lavenderDark },
                }}
            >
                Galia Standard Poodles
            </Typography>

            {isMobile ? (
                <IconButton
                    aria-label={menuOpen ? 'Navigation menu open' : 'Open navigation menu'}
                    aria-expanded={menuOpen}
                    onClick={onOpenMenu}
                    sx={{
                        flexShrink: 0,
                        width: 42,
                        height: 42,
                        borderRadius: 1.5,
                        bgcolor: menuOpen ? 'rgba(128, 121, 154, 0.35)' : 'rgba(128, 121, 154, 0.22)',
                        color: brandColors.lavenderDark,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            bgcolor: menuOpen ? 'rgba(128, 121, 154, 0.42)' : 'rgba(128, 121, 154, 0.32)',
                        },
                    }}
                >
                    <MenuIcon fontSize="small" />
                </IconButton>
            ) : (
                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                    {NAV_ITEMS.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Button
                                key={item.path}
                                component={RouterLink}
                                to={item.path}
                                size="small"
                                sx={navButtonSx(isActive)}
                            >
                                {item.label}
                            </Button>
                        );
                    })}
                    <Button
                        id="explore-menu-button"
                        size="small"
                        aria-haspopup="true"
                        aria-expanded={exploreOpen ? 'true' : 'false'}
                        aria-controls={exploreOpen ? 'explore-menu' : undefined}
                        onClick={handleExploreOpen}
                        endIcon={
                            <KeyboardArrowDownIcon
                                sx={{
                                    fontSize: '1.1rem !important',
                                    transition: 'transform 0.2s ease',
                                    transform: exploreOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        }
                        sx={navButtonSx(isExploreActive || exploreOpen)}
                    >
                        {EXPLORE_MENU_LABEL}
                    </Button>
                    <Menu
                        id="explore-menu"
                        anchorEl={exploreAnchor}
                        open={exploreOpen}
                        onClose={handleExploreClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 0.75,
                                    minWidth: 200,
                                    borderRadius: 2,
                                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                                    border: '1px solid rgba(15, 23, 42, 0.06)',
                                },
                            },
                        }}
                        MenuListProps={{ 'aria-labelledby': 'explore-menu-button' }}
                    >
                        {EXPLORE_ITEMS.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <MenuItem
                                    key={item.path}
                                    component={RouterLink}
                                    to={item.path}
                                    onClick={handleExploreClose}
                                    selected={isActive}
                                    sx={{
                                        fontSize: '0.9375rem',
                                        fontWeight: isActive ? 600 : 500,
                                        py: 1.1,
                                        px: 2,
                                    }}
                                >
                                    {item.label}
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </Stack>
            )}
        </Box>
    );
};

export default FloatingNav;
